import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchBooks,
  setCurrentPage,
  setSelectedBook,
  addBook,
  updateBook,
  deleteBook,
} from '../../redux/study/bookSlice';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const Books = () => {
  const dispatch = useDispatch();

  const {
    books = [],
    totalPages = 1,
    currentPage = 1,
    loading = false,
    error = null,
    selectedBook = null,
  } = useSelector((state) => state.books || {});

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [newBook, setNewBook] = useState({
    title: '',
    author: '',
    published_year: '',
    contents: [
      {
        chapterTitle: '',
        topicTitle: '',
        pageStart: '',
        pageEnd: '',
        theory: '',
      }
    ]
  });

  const handleAddContent = () => {
    setNewBook((prev) => ({
      ...prev,
      contents: [...prev.contents, { chapterTitle: '', topicTitle: '', pageStart: '', pageEnd: '', theory: '' }],
    }));
  };

  const openViewModal = (book) => {
    // Set the selected book in the Redux store to be viewed
    dispatch(setSelectedBook(book));
    setShowViewModal(true);
  };
  
  const handleContentChange = (index, field, value) => {
    const updatedContents = [...newBook.contents];
    updatedContents[index][field] = value;
    setNewBook({ ...newBook, contents: updatedContents });
  };

  const booksPerPage = 5;
  const maxPageNumbers = 5;

  useEffect(() => {
    dispatch(fetchBooks({ search: searchTerm, page: currentPage, limit: booksPerPage }));
  }, [dispatch, searchTerm, currentPage]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      dispatch(setCurrentPage(pageNumber));
    }
  };

  const openAddModal = () => {
    setNewBook({
      title: '',
      author: '',
      published_year: '',
      contents: [
        {
          chapterTitle: '',
          topicTitle: '',
          pageStart: '',
          pageEnd: '',
          theory: ''
        }
      ]
    });
    setShowAddModal(true);
  };

  const openEditModal = (book) => {
    dispatch(setSelectedBook(book));
    setShowEditModal(true);
  };

  const handleEditContentChange = (index, field, value) => {
    if (!selectedBook.contents || !selectedBook.contents[index]) return;
  
    const updatedContents = [...selectedBook.contents];
    updatedContents[index] = { ...updatedContents[index], [field]: value };
  
    dispatch(setSelectedBook({ ...selectedBook, contents: updatedContents }));
  };
  

  const handleAddBook = async () => {
    try {
      await dispatch(addBook(newBook));
      dispatch(fetchBooks({ search: searchTerm, page: currentPage, limit: booksPerPage }));
      setShowAddModal(false);
      toastr.success('Book added successfully!');
    } catch (error) {
      console.error('Error adding book:', error);
      toastr.error('Error adding book!');
    }
  };

  const handleUpdateBook = async () => {
    if (selectedBook) {
      try {
        await dispatch(updateBook({ id: selectedBook.book_id, bookData: selectedBook }));
        dispatch(fetchBooks({ search: searchTerm, page: currentPage, limit: booksPerPage }));
        setShowEditModal(false);
        toastr.success('Book updated successfully!');
      } catch (error) {
        console.error('Error updating book:', error);
        toastr.error('Error updating book!');
      }
    }
  };

  const handleDeleteBook = async (id) => {
    try {
      await dispatch(deleteBook(id));
      dispatch(fetchBooks({ search: searchTerm, page: currentPage, limit: booksPerPage }));
      toastr.success('Book deleted successfully!');
    } catch (error) {
      console.error('Error deleting book:', error);
      toastr.error('Error deleting book!');
    }
  };

  const pageNumbers = [];
  const startPage = Math.max(1, currentPage - Math.floor(maxPageNumbers / 2));
  const endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <section className="content">
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <h3 className="card-title mb-0 mr-2">Books List</h3>
              <button className="btn btn-success btn-sm rounded-circle" style={{ width: '30px', height: '30px', padding: 0 }} onClick={openAddModal}>
                <i className="fas fa-plus"></i>
              </button>
            </div>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search Book"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ maxWidth: '200px' }}
            />
          </div>

          <div className="card-body p-0">
            {loading && <div className="text-center py-3"><div className="spinner-border text-primary" /></div>}
            {error && <p className="text-center text-danger">Error: {error}</p>}

            <table className="table table-sm table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Year</th>
                  <th>Date Created</th>
                  <th className="text-center" style={{ width: '200px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {books.map((book) => (
                  <tr key={book.book_id}>
                    <td>{book.book_id}</td>
                    <td>{book.title}</td>
                    <td>{book.author}</td>
                    <td>{book.published_year}</td>
                    <td>{book.created_at}</td>
                    <td className="text-center">
                      <button className="btn btn-primary btn-sm mx-1" onClick={() => openViewModal(book)}>
                        <i className="fas fa-file"></i>
                      </button>
                      <button className="btn btn-sm btn-info mr-1" onClick={() => openEditModal(book)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDeleteBook(book.book_id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card-footer clearfix">
            <ul className="pagination pagination-sm m-0 float-right">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(1)}>First</button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
              </li>
              {pageNumbers.map((number) => (
                <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => paginate(number)}>{number}</button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(totalPages)}>Last</button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Book</h5>
                <button type="button" className="close" onClick={() => setShowAddModal(false)}>&times;</button>
              </div>
              <div className="modal-body">
                {/* Book Basic Info */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Title</label>
                    <input type="text" className="form-control" value={newBook.title} onChange={(e) => setNewBook({ ...newBook, title: e.target.value })} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Author</label>
                    <input type="text" className="form-control" value={newBook.author} onChange={(e) => setNewBook({ ...newBook, author: e.target.value })} />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Published Year</label>
                    <input type="number" className="form-control" value={newBook.published_year} onChange={(e) => setNewBook({ ...newBook, published_year: e.target.value })} />
                  </div>
                </div>

                {/* Dynamic Content Blocks */}
                <h6>Chapters and Topics</h6>
                {newBook.contents.map((content, index) => (
                  <div className="border p-3 mb-3" key={index}>
                    <div className="row mb-2">
                      <div className="col-md-6">
                        <label>Chapter Title</label>
                        <input type="text" className="form-control" value={content.chapterTitle} onChange={(e) => handleContentChange(index, 'chapterTitle', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label>Topic Title</label>
                        <input type="text" className="form-control" value={content.topicTitle} onChange={(e) => handleContentChange(index, 'topicTitle', e.target.value)} />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-6">
                        <label>Start Page</label>
                        <input type="number" className="form-control" value={content.pageStart} onChange={(e) => handleContentChange(index, 'pageStart', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label>End Page</label>
                        <input type="number" className="form-control" value={content.pageEnd} onChange={(e) => handleContentChange(index, 'pageEnd', e.target.value)} />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label>Theory</label>
                      <textarea className="form-control" rows="3" value={content.theory} onChange={(e) => handleContentChange(index, 'theory', e.target.value)}></textarea>
                    </div>
                  </div>
                ))}
                <button className="btn btn-outline-primary mb-3" onClick={handleAddContent}>+ Add More</button>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleAddBook}>Add</button>
              </div>
            </div>
          </div>
        </div>
      )}

{/* View Modal */}
{showViewModal && selectedBook && (
  <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
    <div className="modal-dialog modal-lg modal-dialog-scrollable">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">View Book</h5>
          <button type="button" className="close" onClick={() => setShowViewModal(false)}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Title</label>
              <p>{selectedBook.title}</p>
            </div>
            <div className="col-md-6">
              <label className="form-label">Author</label>
              <p>{selectedBook.author}</p>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Published Year</label>
              <p>{selectedBook.published_year}</p>
            </div>
          </div>

          {/* Display Content */}
          <h6>Chapters and Topics</h6>
          <table className="table table-sm table-bordered">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Chapter Title</th>
                <th>Topic Title</th>
                <th>Start Page</th>
                <th>End Page</th>
                <th>Theory</th>
                <th>Lichess Link</th>
              </tr>
            </thead>
            <tbody>
              {selectedBook.contents.map((content, index) => (
                <tr key={index}>
                  <td>{index + 1}</td> {/* Serial Number */}
                  <td>{content.chapterTitle}</td>
                  <td>{content.topicTitle}</td>
                  <td>{content.pageStart}</td>
                  <td>{content.pageEnd}</td>
                  <td>{content.theory}</td> {/* Theory column */}
                  <td>
                    {/* Assuming you want to link to a Lichess game */}
                    {content.lichessLink ? (
                      <a href={content.lichessLink} target="_blank" rel="noopener noreferrer">View Game</a>
                    ) : (
                      'N/A'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
        </div>
      </div>
    </div>
  </div>
)}


      {/* Edit Modal */}
      {showEditModal && selectedBook && (
  <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
    <div className="modal-dialog modal-lg modal-dialog-scrollable">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Edit Book</h5>
          <button type="button" className="close" onClick={() => setShowEditModal(false)}>&times;</button>
        </div>
        <div className="modal-body">
          {/* Book Basic Info */}
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Title"
                value={selectedBook.title}
                onChange={(e) =>
                  dispatch(setSelectedBook({ ...selectedBook, title: e.target.value }))
                }
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Author</label>
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Author"
                value={selectedBook.author}
                onChange={(e) =>
                  dispatch(setSelectedBook({ ...selectedBook, author: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Published Year</label>
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Published Year"
                value={selectedBook.published_year}
                onChange={(e) =>
                  dispatch(setSelectedBook({ ...selectedBook, published_year: e.target.value }))
                }
              />
            </div>
          </div>

          <h6>Chapters and Topics</h6>
          {selectedBook.contents.map((content, index) => (
            <div className="border p-3 mb-3" key={index}>
              <div className="row mb-2">
                <div className="col-md-6">
                  <label>Chapter Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={content.chapterTitle}
                    onChange={(e) =>
                      handleEditContentChange(index, 'chapterTitle', e.target.value)
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label>Topic Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={content.topicTitle}
                    onChange={(e) =>
                      handleEditContentChange(index, 'topicTitle', e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-6">
                  <label>Start Page</label>
                  <input
                    type="number"
                    className="form-control"
                    value={content.pageStart}
                    onChange={(e) =>
                      handleEditContentChange(index, 'pageStart', e.target.value)
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label>End Page</label>
                  <input
                    type="number"
                    className="form-control"
                    value={content.pageEnd}
                    onChange={(e) =>
                      handleEditContentChange(index, 'pageEnd', e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="mb-2">
                <label>Theory</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={content.theory}
                  onChange={(e) =>
                    handleEditContentChange(index, 'theory', e.target.value)
                  }
                ></textarea>
              </div>
            </div>
          ))}
          
          {/* Add more chapter/topic fields */}
          <button
  className="btn btn-outline-primary mb-3"
  onClick={() =>
    dispatch(
      setSelectedBook({
        ...selectedBook,
        contents: [
          ...selectedBook.contents,
          {
            chapterTitle: '',
            topicTitle: '',
            pageStart: '',
            pageEnd: '',
            theory: ''
          }
        ]
      })
    )
  }
>
  + Add More Chapter/Topic
</button>

        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
            Close
          </button>
          <button className="btn btn-primary" onClick={handleUpdateBook}>
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
)}

    </section>
  );
};

export default Books;
